import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { APP_CONFIG } from '../config/constants';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 2.5rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  position: relative;
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.95);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.75rem;
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  font-weight: 700;
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const DownloadOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DownloadOption = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const AppIcon = styled.img`
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  object-fit: cover;
`;

const AppInfo = styled.div`
  flex: 1;
`;

const AppName = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0.25rem;
`;

const AppDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;


const QRCodeSection = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px dashed rgba(255, 255, 255, 0.3);
`;

const QRCodeImage = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 12px;
  margin: 0 auto 1rem;
  display: block;
  object-fit: contain;
`;

const QRCodeText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const ComingSoonBadge = styled.span`
  background: linear-gradient(135deg, #7359B3 0%, #593399 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

const DownloadModal = ({ isOpen, onClose }) => {
  // Track conversion when modal opens
  useEffect(() => {
    if (isOpen && window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17757227613/BzAcCMycn8YbEN2Up5NC',
        'value': 1.0,
        'currency': 'USD'
      });
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDownload = (platform) => {
    if (platform === 'iOS') {
      window.open(APP_CONFIG.iosAppStoreUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <ModalTitle>Download {APP_CONFIG.name}</ModalTitle>
        <ModalSubtitle>
          Get the {APP_CONFIG.name} app to start getting peace of mind effortlessly
        </ModalSubtitle>

        <DownloadOptions>
          <DownloadOption onClick={() => handleDownload('iOS')}>
            <AppIcon src="/logo.png" alt={`${APP_CONFIG.name} logo`} />
            <AppInfo>
              <AppName>
                Download for iOS
              </AppName>
              <AppDescription>Available on the App Store</AppDescription>
            </AppInfo>
          </DownloadOption>
        </DownloadOptions>

        <QRCodeSection>
          <QRCodeText>Or scan with your phone:</QRCodeText>
          <QRCodeImage src="/qr-code.png" alt="QR Code for app download" />
          <QRCodeText>Point your camera at this code to download</QRCodeText>
        </QRCodeSection>
      </ModalContent>
    </ModalOverlay>
  );
};

export default DownloadModal;
